import { useTranslation } from 'react-i18next';
import { useDeniedAgentProfile, useFeatureAgent, type Agent } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  useCurrentAgent,
  useCurrentAgentId,
  useIsCurrentAgentMissing,
} from '@/features/chats/stores';

/**
 * The agent a message is attributed to: the one it names, else the chat's current agent.
 *
 * A message outlives its agent's card, so one whose card can no longer be loaded gets a stand-in
 * rather than staying `undefined`, which every consumer would otherwise read as still loading. The
 * stand-in for an agent the caller may not open carries its name and avatar.
 */
export const useMessageAgent = (
  agentId: string | undefined,
): { agent: Agent | undefined; isLoading: boolean } => {
  const { t } = useTranslation('chats');
  const contextAgent = useCurrentAgent();
  const contextAgentId = useCurrentAgentId();
  const isContextAgentMissing = useIsCurrentAgentMissing();
  const workspaceId = useCurrentWorkspaceId();
  const {
    agent: featureAgent,
    isLoading,
    isMissing: isFeatureAgentMissing,
  } = useFeatureAgent(agentId, workspaceId);

  const loaded = agentId ? featureAgent : (contextAgent ?? undefined);
  const isMissing = agentId ? isFeatureAgentMissing : isContextAgentMissing;
  const deniedAgent = useDeniedAgentProfile(
    isMissing ? (agentId ?? contextAgentId) : undefined,
    workspaceId,
  );
  const agent =
    loaded ??
    deniedAgent ??
    (isMissing ? { id: agentId ?? '', title: t('chats.chatBubble.unavailableAgent') } : undefined);

  return { agent, isLoading };
};
